/**
 * Template Calibration scatter view.
 * X = gate pass rate (or selected dimension), Y = primary KPI.
 * Shows R-squared badge, trend line, verdict-colored dots.
 *
 * FR-50: Verdict-colored dots (Scale=green, Fix=amber, Kill=red)
 * FR-51: R-squared badge with thresholds, hidden <20 creatives
 * FR-52: Trend line overlay
 * FR-53: Causation disclaimer
 * FR-54: X-axis selector (gate pass rate + scale dimensions)
 */

import { useState, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Label,
} from 'recharts';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SkeletonCard } from '@/components/dashboard/shared/SkeletonLoaders';
import { useCalibration } from '@/hooks/useDashboard';
import { fmtPct } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { CalibrationPoint } from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

// ============================================================================
// HELPERS
// ============================================================================

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: CalibrationPoint;
}

function CustomDot({ cx = 0, cy = 0, payload }: CustomDotProps) {
  if (!payload) return null;
  const color = VERDICT_COLORS[payload.verdict] ?? '#2563FF';
  return (
    <circle cx={cx} cy={cy} r={7} fill={color} fillOpacity={0.75} stroke={color} strokeWidth={1} />
  );
}

function RSquaredBadge({ rSquared }: { rSquared: number }) {
  const label = rSquared > 0.5 ? 'Well calibrated' : rSquared >= 0.3 ? 'Moderate' : 'Weak';
  const bg = rSquared > 0.5 ? 'bg-success/10' : rSquared >= 0.3 ? 'bg-warning/10' : 'bg-danger/10';
  const text = rSquared > 0.5 ? 'text-success' : rSquared >= 0.3 ? 'text-warning' : 'text-danger';
  return (
    <div className={cn('inline-flex flex-col items-center px-4 py-2 rounded-[10px]', bg)}>
      <span className={cn('text-3xl font-bold', text)}>R² = {rSquared.toFixed(2)}</span>
      <span className={cn('text-xs font-medium', text)}>{label}</span>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TemplateCalibration() {
  const { filters, activeTemplate } = useDashboardContext();

  const [xAxisMode, setXAxisMode] = useState<string>('gatePassRate');

  const { data, isLoading, isError } = useCalibration(
    filters,
    xAxisMode !== 'gatePassRate' ? xAxisMode : undefined
  );

  const calibrationData = data;

  // Scale-type dimensions from template for X-axis selector (FR-54)
  const scaleDimensions = useMemo(
    () => (activeTemplate?.diagnosisDimensions ?? []).filter(
      (d) => d.outputConfig.type === 'scale' && d.enabled
    ),
    [activeTemplate]
  );

  if (isLoading) {
    return <SkeletonCard lines={8} />;
  }

  if (isError || !calibrationData) {
    return (
      <EmptyState
        title="Connect Meta to see template calibration"
        description="Configure a primary KPI in your template and connect Meta performance data to see how well your gates predict market success."
      />
    );
  }

  const { points, rSquared, trendLine, xAxisLabel } = calibrationData;

  if (points.length === 0) {
    return (
      <EmptyState
        title="No KPI data yet"
        description="Import or connect Meta performance data to your creatives to see calibration."
      />
    );
  }

  const hasEnoughForR2 = points.length >= 20;
  const showRSquared = hasEnoughForR2 && rSquared !== null && rSquared !== undefined;

  // Trend line endpoints
  const gatePassRates = points.map((p) => p.gatePassRate);
  const minX = Math.min(...gatePassRates);
  const maxX = Math.max(...gatePassRates);
  const trendMinY = trendLine.slope * minX + trendLine.intercept;
  const trendMaxY = trendLine.slope * maxX + trendLine.intercept;

  // CSV export
  const csvHeaders = ['Creative', 'Verdict', 'Gate Pass Rate', 'KPI Value'];
  const csvRows = points.map((p) => [
    p.creativeName,
    p.verdict,
    p.gatePassRate,
    p.kpiValue,
  ]);

  return (
    <div className="space-y-4">
      <div className="rounded-[14px] bg-surface shadow-sm p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold text-primary">Template Calibration</div>
            <div className="text-xs text-muted mt-0.5">
              {xAxisLabel ?? 'Gate pass rate'} vs Primary KPI
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* X-axis selector (FR-54) */}
            {scaleDimensions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted">X-axis:</span>
                <select
                  value={xAxisMode}
                  onChange={(e) => setXAxisMode(e.target.value)}
                  className="text-xs border border-border-subtle rounded px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand"
                >
                  <option value="gatePassRate">Gate Pass Rate</option>
                  {scaleDimensions.map((d) => (
                    <option key={d.dimensionId} value={d.dimensionId}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}
            <CsvExport headers={csvHeaders} rows={csvRows} filename="template-calibration" />
          </div>
        </div>

        {/* R-squared badge (FR-51) */}
        <div className="flex justify-center mb-4">
          {showRSquared ? (
            <RSquaredBadge rSquared={rSquared} />
          ) : (
            <div className="text-xs text-muted italic">
              Need 20+ creatives for R² (currently {points.length})
            </div>
          )}
        </div>

        {/* Scatter chart */}
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7ECF6" strokeOpacity={0.5} />
            <XAxis
              type="number"
              dataKey="gatePassRate"
              name={xAxisLabel ?? 'Gate Pass Rate'}
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#8B95A7' }}
              tickFormatter={(v: number) => `${v}%`}
            >
              <Label
                value={xAxisLabel ?? 'Gate Pass Rate (%)'}
                offset={-10}
                position="insideBottom"
                style={{ fontSize: 11, fill: '#8B95A7' }}
              />
            </XAxis>
            <YAxis
              type="number"
              dataKey="kpiValue"
              name="KPI Value"
              tick={{ fontSize: 10, fill: '#8B95A7' }}
              width={55}
              axisLine={false}
            >
              <Label
                value={calibrationData?.kpiLabel ?? 'KPI'}
                angle={-90}
                position="insideLeft"
                style={{ fontSize: 11, fill: '#8B95A7' }}
              />
            </YAxis>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const first = payload[0];
                if (!first) return null;
                const d = first.payload as CalibrationPoint;
                return (
                  <div className="rounded-[10px] bg-surface shadow-lg border border-border-subtle p-2 text-xs">
                    <div className="font-semibold text-primary">{d.creativeName}</div>
                    <div className="text-xs font-medium" style={{ color: VERDICT_COLORS[d.verdict] }}>
                      {d.verdict}
                    </div>
                    <div className="mt-1">
                      <div>Gate pass rate: {fmtPct(d.gatePassRate)}</div>
                      <div>KPI: {d.kpiValue?.toFixed(2)}</div>
                    </div>
                  </div>
                );
              }}
            />
            {/* Trend line (FR-52) */}
            <ReferenceLine
              segment={[
                { x: minX, y: Math.max(0, trendMinY) },
                { x: maxX, y: trendMaxY },
              ]}
              stroke="#2563FF"
              strokeWidth={1.5}
            />
            <Scatter data={points} shape={<CustomDot />} />
          </ScatterChart>
        </ResponsiveContainer>

        {/* Causation disclaimer (FR-53) */}
        <div className="mt-3 rounded-[10px] bg-surface-subtle px-3 py-2">
          {showRSquared && (
            <p className="text-xs text-secondary mb-1">
              <strong>R² = {rSquared.toFixed(2)}</strong> --{' '}
              {rSquared > 0.5
                ? 'Gate pass rate is a strong predictor of KPI performance for this template.'
                : rSquared >= 0.3
                ? 'Moderate correlation -- gates are a useful signal but not the only predictor.'
                : 'Weak correlation -- consider reviewing gate definitions or collecting more data.'
              }
            </p>
          )}
          <p className="text-xs text-muted">
            Shows association, not causation. Gate quality and KPI performance may both be influenced
            by spend, targeting, or placement. Use as one signal among many.
          </p>
        </div>

        {/* Verdict legend */}
        <div className="flex items-center gap-4 justify-center mt-3 text-xs text-muted">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-success" />Scale
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-warning" />Fix
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-danger" />Kill
          </span>
        </div>
      </div>
    </div>
  );
}
